import { useMemo, useState, useEffect } from "react";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { getProjects } from "@/lib/portfolio-store";

const Portfolio = () => {
  const [active, setActive] = useState("Todos");
  const [projects, setProjects] = useState(() => getProjects());
  const filters = useMemo(
    () => ["Todos", ...Array.from(new Set(projects.map((project) => project.cat)))],
    [projects]
  );

  useEffect(() => {
    const loaded = getProjects();
    setProjects(loaded);
    // Listen for storage changes (when admin updates projects)
    const handleStorageChange = () => {
      const updated = getProjects();
      setProjects(updated);
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const list = active === "Todos" ? projects : projects.filter((p) => p.cat === active);

  return (
    <section
      id="portfolio"
      data-testid="portfolio-section"
      className="relative bg-[#ff6b35] noise py-24 md:py-32 overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 relative">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-12 vc-reveal">
          <div>
            <div className="inline-block bg-[#0a0a0a] text-white px-3 py-1 brutal-border font-mono text-xs uppercase tracking-widest mb-4">
              ✦ Trabalhos selecionados
            </div>
            <h2 className="font-display text-[#0a0a0a] text-5xl sm:text-7xl uppercase leading-[0.9]">
              Projetos que <br />
              <span className="text-outline">brilham.</span>
            </h2>
          </div>

          <div className="flex flex-wrap gap-2" aria-label="Filtros do portfolio">
            {filters.map((f) => (
              <button
                key={f}
                data-testid={`filter-${f.toLowerCase()}`}
                type="button"
                onClick={() => setActive(f)}
                className={`px-4 py-2 brutal-border font-mono uppercase text-xs tracking-widest transition-colors focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-white ${
                  active === f
                    ? "bg-white text-[#0a0a0a] brutal-shadow"
                    : "bg-[#0a0a0a] text-white hover:bg-white hover:text-[#0a0a0a]"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[minmax(0,720px)] md:justify-center gap-6">
          {list.map((p, i) => (
            <Link
              key={p.title}
              to={p.casePath || p.url}
              data-testid={`project-card-${i}`}
              className={`vc-reveal group brutal-card bg-[#0a0a0a] block overflow-hidden focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-white ${
                i % 3 === 1 ? "lg:translate-y-8" : ""
              }`}
            >
              <div className="relative overflow-hidden aspect-[4/3]">
                <img
                  src={p.img}
                  alt={`Projeto web B2B desenvolvido pela BezierClick: ${p.title} (${p.cat})`}
                  width="1200"
                  height="900"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-[#0a0a0a]/30 group-hover:bg-[#0a0a0a]/60 transition-colors flex items-center justify-center">
                  <span className="opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity bg-[#ff6b35] text-[#0a0a0a] brutal-border px-5 py-3 font-display uppercase text-sm flex items-center gap-2">
                    Ver projeto <ArrowUpRight size={18} />
                  </span>
                </div>
              </div>
              <div className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-display text-white text-xl uppercase text-pretty">{p.title}</h3>
                  <span className="font-mono text-[10px] uppercase text-[#ff6b35] tracking-widest">
                    {p.cat}
                  </span>
                </div>
                <p className="text-white/75 text-sm leading-snug">{p.description}</p>
                {p.result && (
                  <p className="mt-3 text-white/70 text-sm leading-snug">
                    <strong className="text-[#ff6b35]">Resultado:</strong> {p.result}
                  </p>
                )}
                <div className="flex flex-wrap gap-2 mt-3">
                  {p.tags.map((t) => (
                    <span
                      key={t}
                      className="bg-[#ff6b35] text-[#0a0a0a] font-mono text-[10px] uppercase px-2 py-1 brutal-border"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Portfolio;
